import * as locales from 'date-fns/locale';
import { useCallback } from 'react';

import { useI18n } from './use-i18n';
import { usePageContext } from './use-page-context';

export enum Locale {
  DE_DE = 'de-DE',
  EN_US = 'en-US',
  ES_US = 'es-US',
  FR_FR = 'fr-FR',
  IT_IT = 'it-IT',
  JA_JP = 'ja-JP',
  KO_KR = 'ko-KR',
  PT_BR = 'pt-BR',
  ZH_CN = 'zh-CN',
  ZH_TW = 'zh-TW',
}

export const dateFnsMap = {
  'en-US': locales.enUS,
  'de-DE': locales.deAT,
  'es-US': locales.es,
  'fr-FR': locales.fr,
  'it-IT': locales.it,
  'ja-JP': locales.ja,
  'ko-KR': locales.ko,
  'pt-BR': locales.ptBR,
  'zh-CN': locales.zhCN,
  'zh-TW': locales.zhTW,
};

const LOCALE_RE = new RegExp(
  `(${Object.values(Locale)
    .map((locale) => locale.replace('-', '\\-'))
    .map((locale) => [locale, locale.toLowerCase()])
    .flat()
    .join('|')})`,
);

export const getLocaleLanguage = (locale: Locale = Locale.EN_US): string =>
  locale.split('-')[0]!;
export const getLocaleCountry = (locale: Locale = Locale.EN_US): string =>
  locale.split('-')[1]!;

export const parseLocale = (locale: string): Locale => {
  switch (locale) {
    case 'de':
    case 'de-de':
    case 'de-DE':
      return Locale.DE_DE;
    case 'en':
    case 'en-us':
    case 'en-US':
    case 'en-gb':
    case 'en-GB':
      return Locale.EN_US;
    case 'es':
    case 'es-es':
    case 'es-ES':
    case 'es-us':
    case 'es-US':
      return Locale.ES_US;
    case 'fr':
    case 'fr-fr':
    case 'fr-FR':
      return Locale.FR_FR;
    case 'it':
    case 'it-it':
    case 'it-IT':
      return Locale.IT_IT;
    case 'ja':
    case 'ja-jp':
    case 'ja-JP':
      return Locale.JA_JP;
    case 'ko':
    case 'ko-kr':
    case 'ko-KR':
      return Locale.KO_KR;
    case 'pt':
    case 'pt-br':
    case 'pt-BR':
      return Locale.PT_BR;
    case 'zh':
    case 'zh-cn':
    case 'zh-CN':
      return Locale.ZH_CN;
    case 'zh-tw':
    case 'zh-TW':
      return Locale.ZH_TW;
    default:
      throw new Error(`unsupported locale: ${JSON.stringify(locale)}`);
  }
};

export const safeParseLocale = (locale?: string | null): Locale => {
  try {
    return parseLocale(locale ?? 'en-US');
  } catch {
    return Locale.EN_US;
  }
};

export const formatLocale = (
  locale: Locale,
  options: Intl.DisplayNamesOptions,
): string => {
  switch (locale) {
    case 'zh-TW':
      return '繁體中文';
    case 'zh-CN':
      return '简体中文';
    default:
      return new Intl.DisplayNames(locale, options).of(locale)!;
  }
};

export const addLocaleToUrlPathname = (
  pathname: string,
  locale: Locale,
): string => {
  if (locale === Locale.EN_US) return removeLocaleFromUrlPathname(pathname);
  else {
    return `/${locale.toLowerCase()}${removeLocaleFromUrlPathname(pathname)}`;
  }
};

export const removeLocaleFromUrlPathname = (pathname: string) =>
  pathname.replace(new RegExp(`^/${LOCALE_RE.source}`), '');

export const isLocale = (locale: unknown): locale is Locale => {
  return Object.values(Locale)
    .map((locale) => locale.toLowerCase())
    .includes(((locale as string | undefined) ?? '').toLowerCase());
};

export const useLocalizedURL = () => {
  const { t } = useI18n();
  const { locale } = usePageContext();

  if (!isLocale(locale)) {
    throw new Error('expected valid Locale');
  }

  const getLocalizedPath = useCallback(
    (path: string) => {
      const localizedPath = t(`${path}.pathname`, {
        namespace: 'pathnames',
      });

      // If localizedPath is undefined or empty, return the original path
      if (!localizedPath || localizedPath.includes('Translation missing')) {
        return path;
      }

      return addLocaleToUrlPathname(localizedPath, locale);
    },
    [locale, t],
  );

  return { getLocalizedPath };
};

export const ctaLocaleMap = {
  'es-US': {
    text: 'Comprar Boletos',
    href: '/es-us/comprar-boletos/',
  },
  'fr-FR': {
    text: 'Acheter Des Billets',
    href: '/fr-fr/acheter-des-billets/',
  },
  'de-DE': {
    text: 'Tickets Kaufen',
    href: '/de-de/tickets-kaufen/',
  },
  'it-IT': {
    text: 'Comprare Biglietti',
    href: '/it-it/comprare-biglietti/',
  },
  'ja-JP': {
    text: 'チケットを買う',
    href: '/ja-jp/%E3%83%81%E3%82%B1%E3%83%83%E3%83%88%E3%82%92%E8%B2%B7%E3%81%86/',
  },
  'ko-KR': {
    text: '티켓-구매하기',
    href: '/ko-kr/%ED%8B%B0%EC%BC%93-%EA%B5%AC%EB%A7%A4%ED%95%98%EA%B8%B0/',
  },
  'pt-BR': {
    text: 'Comprar Ingressos',
    href: '/pt-br/comprar-ingressos/',
  },
  'zh-CN': {
    text: '买票',
    href: '/zh-cn/%E4%B9%B0%E7%A5%A8/',
  },
  'zh-TW': {
    text: '買票',
    href: '/zh-tw/%E8%B2%B7%E7%A5%A8/',
  },
  'default': {
    text: 'Buy Tickets',
    href: '/buy-tickets/',
  },
};

export const hiddenFeesMap = {
  'en-US': 'No Hidden Fees',
  'es-US': 'Sin costes ocultos',
  'fr-FR': 'Aucuns frais cachés',
  'de-DE': 'Keine versteckten Gebühren',
  'it-IT': 'Nessun costo aggiuntivo',
  'ja-JP': '隠れた手数料なし',
  'ko-KR': '숨겨진 수수료 없음',
  'pt-BR': 'Sem taxas ocultas',
  'zh-CN': '无隐性费用',
  'zh-TW': '沒有任何隱含費用',
};
